/**
 * @file useOfficeApi.ts
 * Custom React hook and utility functions for interacting with office-related API endpoints.
 * Provides methods for fetching auto-suggest options, office lists, and office details,
 * as well as mapping localized office details to a standardized format.
 */
import { useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  Office,
  AutoSuggestOption,
  OfficeBranchDetails,
  LocalizedOfficeDetails
} from '../utils/types';
import { getLocalizedField } from '../utils/helper';
import { useTranslation } from 'react-i18next';

interface UseOfficeApiParams {
  setAutoSuggestOptions?: (options: AutoSuggestOption[]) => void;
  setOfficeResults?: (results: Office[]) => void;
  setOfficeDetails?: (details: OfficeBranchDetails) => void;
}

/**
 * Maps localized office details and branch information to a standardized OfficeBranchDetails object.
 *
 * @param data - The localized office details object.
 * @param branchId - The branch number to find details for.
 * @param companyId - The company ID (branchOyno) to find details for.
 * @param language - The language code for localization.
 * @param ddlData - Additional dropdown list data.
 * @param isDdlServiceAvailable - Indicates if the DDL service is available.
 * @returns The mapped OfficeBranchDetails object.
 */
export const mapOfficeDetails = (
  data: LocalizedOfficeDetails,
  branchId: string,
  companyId: string,
  language: string
): OfficeBranchDetails => {
  const branch = (data.bankBranchDetails || []).find(
    b => b.branchNumber === branchId && b.branchOyno === companyId
  );

  const getField = (
    obj:
      | Partial<LocalizedOfficeDetails>
      | Partial<OfficeBranchDetails>
      | undefined,
    field: string
  ): string => {
    if (!obj) return '';
    const value =
      getLocalizedField(obj, field, language) ??
      (obj as Record<string, unknown>)[field] ??
      '';
    return typeof value === 'string' ? value : '';
  };

  const otherOffices = Array.from(
    new Map(
      (data.bankBranchDetails || [])
        .filter(
          b =>
            !(b.branchNumber === branchId && b.branchOyno === companyId) &&
            !!b.branchNumber &&
            !!b.branchOyno
        )
        .map(b => {
          const office = {
            branchNumber: b.branchNumber,
            companyId: b.branchOyno,
            branchName: getLocalizedField(b, 'branchName', language) ?? ''
          };

          return [[office.branchNumber, office.companyId].join(':'), office] as const;
        })
    ).values()
  ).sort((a, b) => (a.branchName || '').localeCompare(b.branchName || ''));

  const ofcDetail: OfficeBranchDetails = {
    bankName: getField(data, 'bankName'),
    email: data.email ?? '',
    bankSiteUrl: data.bankSiteUrl ?? '',
    bankSiteLabel: data.bankSiteLabel ?? '',
    hostname: data.bankSiteUrl ? window.location.hostname : undefined,
    headOffice: branch?.headOffice,
    contactService: branch?.contactService,
    companyId: branch?.branchOyno ?? '',
    address: getField(branch, 'address'),
    bankNumber: branch?.bankNumber ?? '',
    branchName: getField(branch, 'branchName'),
    branchNumber: branch?.branchNumber ?? '',
    branchOpeningTime: (() => {
      if (Array.isArray(branch?.branchOpeningTime)) return branch.branchOpeningTime;
      if (branch?.branchOpeningTime) return [branch.branchOpeningTime];
      return undefined;
    })(),
    ceoName: getField(branch, 'ceoName'),
    openingTimeAdditionalInformation: getField(
      branch,
      'openingTimeAdditionalInformation'
    ),
    phone: getField(branch, 'phone'),
    postOffice: getField(branch, 'postOffice'),
    postalCode: getField(branch, 'postalCode'),
    serviceNumberCallService: getField(branch, 'serviceNumberCallService'),
    serviceNumberCorporateService: getField(
      branch,
      'serviceNumberCorporateService'
    ),
    serviceNumberCreditService: getField(branch, 'serviceNumberCreditService'),
    serviceNumberFreeHeading: getField(branch, 'serviceNumberFreeHeading'),
    serviceNumberFreePhoneNumber: getField(
      branch,
      'serviceNumberFreePhoneNumber'
    ),
    serviceNumberInsuranceService: getField(
      branch,
      'serviceNumberInsuranceService'
    ),
    serviceNumberLawService: getField(branch, 'serviceNumberLawService'),
    serviceNumberOpRealEstate: getField(branch, 'serviceNumberOpRealEstate'),
    serviceNumberSavingService: getField(branch, 'serviceNumberSavingService'),
    otherOffices
  };

  return ofcDetail;
};

/**
 * Custom React hook for interacting with office-related API endpoints.
 * Provides methods to fetch auto-suggest options, office lists, and office details,
 * and updates the provided state setters accordingly.
 *
 * @param params - Object containing optional state setter functions:
 *   - setAutoSuggestOptions: Setter for auto-suggest options.
 *   - setOfficeResults: Setter for office search results.
 *   - setOfficeDetails: Setter for office branch details.
 * @returns An object with methods:
 *   - fetchAutoSuggest: Fetches auto-suggest options based on a query string.
 *   - fetchOfficeList: Fetches a list of offices based on a query string.
 *   - fetchOfficeDetails: Fetches detailed information for a specific office branch.
 */
export function useOfficeApi({
  setAutoSuggestOptions,
  setOfficeResults,
  setOfficeDetails
}: UseOfficeApiParams) {
  const { query, api, setLoading, setError, currentLanguage } = useAppContext();
  const { t } = useTranslation();
  const hasExistingSuggestionsRef = useRef(false);

  const fetchAutoSuggest = useCallback(
    async (query: string, hasExistingSuggestions = false) => {
      hasExistingSuggestionsRef.current = hasExistingSuggestions;
      try {
        const data = await api.fetchAutoSuggest(query, currentLanguage);
        setAutoSuggestOptions?.(data);
        return data;
      } catch (err: unknown) {
        // Only show error if there are no existing suggestions visible
        // This prevents error alerts when user types space after a valid suggestion
        if (!hasExistingSuggestionsRef.current) {
          setError(t('ddl.virheilmoitus'));
        }
        return [];
      }
    },
    [query, currentLanguage, api, setAutoSuggestOptions, setError, t]
  );

  const fetchOfficeList = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const data = await api.fetchOfficeList(query, currentLanguage);
        const mapData = data?.map((item: Record<string, unknown>) => ({
          branchNumber: item.branchNumber as string,
          bankName: getLocalizedField(item, 'bankName', currentLanguage) ?? '',
          branchName:
            getLocalizedField(item, 'branchName', currentLanguage) ?? '',
          postOffice:
            getLocalizedField(item, 'postOffice', currentLanguage) ?? '',
          postalCode: item.postalCode as string,
          streetAddress:
            getLocalizedField(item, 'streetAddress', currentLanguage) ?? '',
          companyId: item.bankNumber as string,
          branchOrganizationIdentificationNumber:
            item.branchOrganizationIdentificationNumber as string
        }));
        setOfficeResults?.(mapData);
        setLoading(false);
      } catch (err: unknown) {
        setLoading(false);
        setError(t('ddl.virheilmoitus'));
      }
    },
    [query, currentLanguage, api, setLoading, setOfficeResults, setError]
  );

  const fetchOfficeDetails = useCallback(
  async (branchId: string, companyId: string) => {
    setLoading(true);
    try {
      const data = await api.fetchOfficeDetails(
        branchId,
        companyId,
        currentLanguage
      );

      if (Object.values(data).every(value => value === null)) {
        setOfficeDetails?.(undefined as unknown as OfficeBranchDetails);
        setError(t('ddl.ilmoitus_eiloydy'));
        setLoading(false);
        return;
      }

      const officeDetails = mapOfficeDetails(
        data,
        branchId,
        companyId,
        currentLanguage
      );
      setOfficeDetails?.(officeDetails);
      setLoading(false);
    } catch (err: unknown) {
      setLoading(false);
      setError(t('ddl.virheilmoitus'));
    }
  },
  [api, currentLanguage, setLoading, setOfficeDetails, setError, t]
);

  return { fetchAutoSuggest, fetchOfficeList, fetchOfficeDetails };
}
